import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Advanced, Badge, Btn } from "./common";
import { useI18n } from "@/lib/i18n";

const SPOTIFY_REASONS = {
  connected: ["success", "Spotify bağlandı."], denied: ["error", "Spotify izin ekranında onay verilmedi."],
  state_invalid: ["error", "Güvenlik doğrulaması geçersiz; işlem iptal edildi."],
  state_expired: ["error", "Yetkilendirme süresi doldu; tekrar deneyin."],
  exchange_failed: ["error", "Spotify kod değişimi başarısız; tekrar deneyin."],
  identity_failed: ["error", "Spotify hesabı doğrulanamadı."], not_configured: ["error", "Sunucuda Spotify yapılandırması eksik."],
  bad_request: ["error", "Geri dönüş isteği eksik/bozuk."],
};

/* Spotify: connection status, requirements and honest premium/device notes. No playback starts here. */
export const SpotifyCard = () => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const load = useCallback(async () => {
    try { setS((await api.get("/spotify/status")).data); } catch { setS({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("spotify");
    if (!p) return;
    const [kind, msg] = SPOTIFY_REASONS[p] || ["error", "Spotify bağlantısı tamamlanmadı."];
    toast[kind === "success" ? "success" : "error"](tx(msg));
    window.history.replaceState({}, "", window.location.pathname);
  }, [tx]);
  const connect = async () => {
    try { window.location.href = (await api.post("/spotify/connect")).data.auth_url; }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "Bağlantı başlatılamadı")); }
  };
  const disconnect = async () => {
    try { const r = await api.post("/spotify/disconnect"); toast.success(tx(r.data?.note || "Bağlantı kesildi")); load(); }
    catch { toast.error(tx("İşlem başarısız")); }
  };
  if (!s) return <div className="text-xs text-white/40">{tx("Durum alınıyor…")}</div>;
  if (s.error) return <div className="text-xs text-red-300">{tx("Durum alınamadı.")}</div>;
  return (
    <div data-testid="spotify-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge color={s.connected ? "green" : "gray"}>{s.connected ? `${tx("bağlı")} · ${s.display_name || ""}` : tx("bağlı değil")}</Badge>
        {s.connected ? <Btn variant="ghost" onClick={disconnect} data-testid="spotify-disconnect">{tx("Bağlantıyı kes")}</Btn>
          : <Btn onClick={connect} disabled={!s.config_ready} data-testid="spotify-connect">{tx("Spotify'ı bağla")}</Btn>}
      </div>
      {s.connected && !s.premium && <p className="text-[0.7rem] text-amber-200/80 mt-2">{tx("Hesap Premium değil: arama ve durum çalışır, çalma kontrolü Spotify kuralı gereği çalışmaz.")}</p>}
      {!s.config_ready && <p className="text-[0.7rem] text-white/50 mt-2">{tx("Sunucuda Spotify geliştirici uygulaması yapılandırılmadı.")}</p>}
      <Advanced testid="spotify-advanced">
        {(s.requirements || []).map((r) => <div key={r}>• {tx(r)}</div>)}
        <div>{Object.entries(s.config || {}).map(([k, v]) => <Badge key={k} color={v ? "green" : "gray"}>{k}: {v ? tx("var") : tx("yok")}</Badge>)}</div>
        <div>{tx("Durum her zaman Spotify'dan okunur; 'çalıyor' bilgisi uydurulmaz. Yenileme anahtarı yalnızca sunucuda tutulur.")}</div>
      </Advanced>
    </div>
  );
};

/* WhatsApp: official Business Cloud API only. Personal WhatsApp has no official API → shown clearly. */
export const WhatsAppCard = () => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const [convs, setConvs] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/whatsapp/status");
      setS(r.data);
      if (r.data?.bound_to_you) setConvs((await api.get("/whatsapp/conversations")).data?.conversations || []);
    } catch { setS({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const bind = async () => {
    try { await api.post("/whatsapp/bind"); toast.success(tx("İş numarası hesabınıza bağlandı")); load(); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "İşlem başarısız")); }
  };
  if (!s) return <div className="text-xs text-white/40">{tx("Durum alınıyor…")}</div>;
  if (s.error) return <div className="text-xs text-red-300">{tx("Durum alınamadı.")}</div>;
  return (
    <div data-testid="whatsapp-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge color={s.bound_to_you ? "green" : "gray"}>{s.bound_to_you ? tx("İş numarası bağlı") : tx("bağlı değil")}</Badge>
        {s.configured && !s.bound_to_you && !s.bound_to_other && <Btn onClick={bind} data-testid="whatsapp-bind">{tx("Bu iş numarasını bağla")}</Btn>}
      </div>
      <p className="text-[0.7rem] text-amber-200/80 mt-2" data-testid="whatsapp-personal-note">
        {tx("Kişisel WhatsApp hesapları için resmî API yoktur; kişisel sohbetler JARVIS'e bağlanamaz.")}
      </p>
      {s.bound_to_you && (
        <div className="mt-2 space-y-1" data-testid="whatsapp-convs">
          {convs.length === 0 ? <div className="text-xs text-white/45">{tx("Henüz gelen mesaj yok.")}</div>
            : convs.slice(0, 5).map((c) => <div key={c.wa_id} className="text-xs text-white/75">{c.name || c.wa_id}</div>)}
        </div>
      )}
      <Advanced testid="whatsapp-advanced">
        {(s.limitations || []).map((l) => <div key={l}>• {tx(l)}</div>)}
        <div>{Object.entries(s.config || {}).map(([k, v]) => <Badge key={k} color={v ? "green" : "gray"}>{k}: {v ? tx("var") : tx("yok")}</Badge>)}</div>
        <div>{tx("Gönderim yalnızca taslak → ekranda onay → tek kullanımlık token ile yapılır.")}</div>
      </Advanced>
    </div>
  );
};

/* Morning brief: schedule on the existing server scheduler. Prepared server-side even with the browser closed;
   it is only spoken when you press Listen in an open app. */
export const MorningBriefCard = () => {
  const { tx } = useI18n();
  const [cfg, setCfg] = useState({ enabled: false, time: "07:30", timezone: "Europe/London" });
  useEffect(() => {
    api.get("/settings").then((r) => { if (r.data?.morning_brief) setCfg((c) => ({ ...c, ...r.data.morning_brief })); }).catch(() => {});
  }, []);
  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    try { const r = await api.put("/briefs/settings", next); setCfg({ enabled: r.data.enabled, time: r.data.time, timezone: r.data.timezone }); toast.success(tx("Kaydedildi")); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "Kaydedilemedi")); }
  };
  const now = async () => {
    try { await api.post("/briefs/generate"); toast.success(tx("Sabah özeti hazırlandı — Bildirimler'de Dinle")); }
    catch { toast.error(tx("Özet hazırlanamadı")); }
  };
  return (
    <div data-testid="morning-brief-card" className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="brief-enabled" />
          {tx("Her gün")}
        </label>
        <input type="time" value={cfg.time} onChange={(e) => setCfg({ ...cfg, time: e.target.value })} onBlur={() => cfg.enabled && save({})}
          aria-label={tx("Saat")} className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-white" data-testid="brief-time" />
        <span className="text-[0.68rem] text-white/45">{cfg.timezone}</span>
        <Btn variant="ghost" onClick={now} data-testid="brief-now">{tx("Şimdi hazırla")}</Btn>
      </div>
      <p className="text-[0.68rem] text-white/45">{tx("Tarayıcı kapalıyken özet sunucuda hazırlanır ve bildirim olarak kaydedilir; sesli okuma yalnızca 'Dinle' dediğinizde olur.")}</p>
    </div>
  );
};

/* Music provider choice: which service the assistant's music commands go to. Choosing never starts playback. */
export const MusicProviderCard = () => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const load = useCallback(async () => { try { setS((await api.get("/music/status")).data); } catch { setS({ error: true }); } }, []);
  useEffect(() => { load(); }, [load]);
  const pick = async (provider) => {
    try { await api.put("/music/provider", { provider }); toast.success(tx("Kaydedildi")); load(); }
    catch (e) { toast.error(tx(e?.response?.data?.detail || "Kaydedilemedi")); }
  };
  if (!s) return <div className="text-xs text-white/40">{tx("Durum alınıyor…")}</div>;
  if (s.error) return <div className="text-xs text-red-300">{tx("Durum alınamadı.")}</div>;
  const OPTS = [["spotify", "Spotify"], ["apple_music", "Apple Music"], ["none", tx("Kapalı")]];
  return (
    <div data-testid="music-provider-card">
      <div className="flex gap-1.5 flex-wrap" role="radiogroup" aria-label={tx("Müzik sağlayıcısı")}>
        {OPTS.map(([id, label]) => (
          <button key={id} role="radio" aria-checked={s.provider === id} onClick={() => pick(id)} data-testid={`music-provider-${id}`}
            className={`px-3 py-1.5 rounded-full border text-xs ${s.provider === id ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-white/60"}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="text-[0.7rem] text-white/45 mt-2">{tx("Asistanın müzik komutları seçili sağlayıcıya gider. Seçim çalmayı başlatmaz.")}</p>
    </div>
  );
};

/* Apple Music (MusicKit JS v3): explicit connect in this browser; state comes only from the MusicKit player. */
export const AppleMusicCard = () => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  const [player, setPlayer] = useState({ loaded: false, connected: false });
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null);
  const load = useCallback(async () => {
    try { setS((await api.get("/apple-music/status")).data); } catch { setS({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let unsub = () => {};
    import("@/lib/appleMusic").then((m) => { unsub = m.subscribe(setPlayer); });
    return () => unsub();
  }, []);
  const PHASE_LABEL = {
    start: "Başlatılıyor…", token: "Sunucudan jeton alınıyor…", script: "MusicKit yükleniyor…",
    configure: "Hazırlanıyor…", authorize: "Apple izni bekleniyor…",
  };
  const connect = async () => {
    // Never fail silently: if the server has no MusicKit keys, say so on the click itself.
    if (!s.config_ready) {
      toast.error(tx("Apple Music sunucuda yapılandırılmamış (MusicKit anahtarları eksik: Team ID, Key ID, .p8). Bağlanmadan önce bu anahtarların eklenmesi gerekir."));
      return;
    }
    setBusy(true); setPhase("start");
    try {
      const m = await import("@/lib/appleMusic");
      const ok = await m.connect(setPhase);
      toast[ok ? "success" : "error"](tx(ok ? "Apple Music bağlandı (bu tarayıcıda)." : "Apple Music izni verilmedi."));
      load();
    } catch (e) { toast.error(tx(e?.message || e?.response?.data?.detail || "Bağlantı başlatılamadı")); }
    finally { setBusy(false); setPhase(null); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { const m = await import("@/lib/appleMusic"); await m.disconnect(); toast.success(tx("Bağlantı kesildi")); load(); }
    catch { toast.error(tx("İşlem başarısız")); }
    finally { setBusy(false); }
  };
  if (!s) return <div className="text-xs text-white/40">{tx("Durum alınıyor…")}</div>;
  if (s.error) return <div className="text-xs text-red-300">{tx("Durum alınamadı.")}</div>;
  const live = player.loaded && player.connected;
  return (
    <div data-testid="apple-music-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge color={live ? "green" : "gray"} testid="apple-music-state">
          {live ? tx("bağlı (bu tarayıcı)") : s.browser_authorized_reported ? tx("bu oturumda doğrulanmadı") : tx("bağlı değil")}
        </Badge>
        {live ? <Btn variant="ghost" onClick={disconnect} disabled={busy} data-testid="apple-music-disconnect">{tx("Bağlantıyı kes")}</Btn>
          : <Btn onClick={connect} disabled={busy} data-testid="apple-music-connect">{busy ? tx(PHASE_LABEL[phase] || "İşleniyor…") : tx("Apple Music'i bağla")}</Btn>}
      </div>
      {!s.config_ready && <p className="text-[0.7rem] text-amber-200/80 mt-2" data-testid="apple-music-config-note">{tx("Sunucuda Apple Music (MusicKit) anahtarı yapılandırılmadı. Bağlan'a basınca neyin eksik olduğu gösterilir.")}</p>}
      {s.browser_authorized_reported && !live && s.config_ready && (
        <p className="text-[0.7rem] text-amber-200/80 mt-2">{tx("Daha önce bağlanmıştı; durumu doğrulamak için yeniden bağlanın (MusicKit bu sayfada henüz yüklenmedi).")}</p>
      )}
      <Advanced testid="apple-music-advanced">
        {(s.requirements || []).map((r) => <div key={r}>• {tx(r)}</div>)}
        <div>{Object.entries(s.config || {}).map(([k, v]) => <Badge key={k} color={v ? "green" : "gray"}>{k}: {v ? tx("var") : tx("yok")}</Badge>)}</div>
        <div>{tx("Geliştirici jetonu sunucuda imzalanır; özel anahtar tarayıcıya gönderilmez. Kullanıcı jetonu yalnızca bu tarayıcıda MusicKit içinde kalır.")}</div>
      </Advanced>
    </div>
  );
};
